import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import axiosInstance from "./api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const QUEUE_DELAY_MS = 30000;

const BulkSendContext = createContext(null);

export const useBulkSend = () => {
    const ctx = useContext(BulkSendContext);
    if (!ctx) throw new Error("useBulkSend must be used inside BulkSendProvider");
    return ctx;
};

export const BulkSendProvider = ({ children }) => {
    // null = no active session | object = active/done session
    const [session, setSession] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const abortRef = useRef(false);

    // ── Start a bulk send ────────────────────────────────────────────────────
    const startBulkSend = useCallback(async ({ badges, eventName, eventId, onSendSuccess }) => {
        if (!badges || badges.length === 0) return;

        abortRef.current = false;

        const initialItems = badges.map((b) => ({
            badgeId:   b.badge._id,
            memberId:  b.person.memberId,
            name:      b.person.name || "Unknown",
            phone:     b.person.phone,
            status:    "pending",  // pending | sending | waiting | success | failed
            error:     null,
            countdown: 0,
        }));

        setSession({ items: initialItems, currentIndex: 0, done: false, eventName });
        setIsModalOpen(true);

        for (let i = 0; i < badges.length; i++) {
            if (abortRef.current) break;

            const { badge, person } = badges[i];

            // Mark current as "sending"
            setSession((prev) => {
                if (!prev) return prev;
                const updated = [...prev.items];
                updated[i] = { ...updated[i], status: "sending" };
                return { ...prev, items: updated, currentIndex: i };
            });

            try {
                const sendRes = await axiosInstance.post("/badge/send-whatsapp", {
                    phone:     person.phone,
                    badgeId:   badge._id,
                    name:      person.name,
                    eventName: eventName || "Event",
                    ...(eventId ? { eventId } : {}),
                });

                const ws = sendRes.data?.whatsappSentAt;
                if (onSendSuccess && ws) {
                    onSendSuccess({ memberId: person.memberId, badgeId: badge._id, whatsappSentAt: ws });
                }

                setSession((prev) => {
                    if (!prev) return prev;
                    const updated = [...prev.items];
                    updated[i] = { ...updated[i], status: "success" };
                    return { ...prev, items: updated };
                });

            } catch (err) {
                const status = err.response?.status;
                const msg    = err.response?.data?.message || err.message;

                setSession((prev) => {
                    if (!prev) return prev;
                    const updated = [...prev.items];
                    updated[i] = { ...updated[i], status: "failed", error: msg };
                    return { ...prev, items: updated };
                });

                // Stop queue on rate-limit
                if (status === 429) {
                    setSession((prev) => {
                        if (!prev) return prev;
                        const updated = [...prev.items];
                        for (let j = i + 1; j < updated.length; j++) {
                            updated[j] = { ...updated[j], status: "failed", error: "Skipped — rate limit" };
                        }
                        return { ...prev, items: updated, done: true };
                    });
                    return;
                }
            }

            // ── Countdown before next send ──────────────────────────────────
            if (i < badges.length - 1 && !abortRef.current) {
                for (let remaining = Math.ceil(QUEUE_DELAY_MS / 1000); remaining > 0; remaining--) {
                    if (abortRef.current) break;
                    setSession((prev) => {
                        if (!prev) return prev;
                        const updated = [...prev.items];
                        if (updated[i + 1]) {
                            updated[i + 1] = { ...updated[i + 1], status: "waiting", countdown: remaining };
                        }
                        return { ...prev, items: updated };
                    });
                    await sleep(1000);
                }
                // Reset next item to pending before it becomes "sending"
                setSession((prev) => {
                    if (!prev) return prev;
                    const updated = [...prev.items];
                    if (updated[i + 1]?.status === "waiting") {
                        updated[i + 1] = { ...updated[i + 1], status: "pending", countdown: 0 };
                    }
                    return { ...prev, items: updated };
                });
            }
        }

        setSession((prev) => prev ? { ...prev, done: true } : prev);
    }, []);

    const clearSession = useCallback(() => {
        abortRef.current = true;
        setSession(null);
        setIsModalOpen(false);
    }, []);

    const openModal  = useCallback(() => setIsModalOpen(true),  []);
    const closeModal = useCallback(() => setIsModalOpen(false), []);

    return (
        <BulkSendContext.Provider value={{ session, isModalOpen, startBulkSend, clearSession, openModal, closeModal }}>
            {children}
        </BulkSendContext.Provider>
    );
};